import React, { useCallback, useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { errMsg } from "../../lib/portfolio";

function DraftCard({ d, founder, onApproved }) {
  const [caption, setCaption] = useState(d.draft_caption || "");
  const [alt, setAlt] = useState((d.alt_text_candidates || [])[0] || "");
  const [applySummary, setApplySummary] = useState(false);
  const [busy, setBusy] = useState(false);
  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/portfolio/ai_drafts/${d.id}/approve`, { caption, alt_text: alt, apply_to_project_summary: applySummary });
      toast.success("Draft approved and applied.");
      onApproved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const approved = !!d.approved_at;
  return (
    <div className="rounded-md border border-erf-borderCream bg-white p-3 space-y-2" data-testid={`portfolio-ai-draft-${d.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className={`text-[11px] font-nav font-semibold uppercase tracking-wider px-2 py-0.5 rounded-sm border ${approved ? "bg-[#ECFDF5] text-[#065F46] border-[#6EE7B7]" : "bg-[#FEF3C7] text-[#78350F] border-[#FCD34D]"}`}
          data-testid={`portfolio-ai-draft-badge-${d.id}`}>
          {approved ? "Approved by Founder" : "AI draft — pending Founder approval"}
        </span>
        <span className="text-[11px] font-mono text-erf-dark/70">{d.source} · {d.model}</span>
      </div>
      {d.error && <p className="text-xs font-nav text-[#9A3412]">Note: {d.error}</p>}
      {d.draft_long_description && <p className="text-sm font-nav text-erf-dark">{d.draft_long_description}</p>}
      {(d.draft_tags?.length > 0 || d.confidence != null) && (
        <p data-testid={`ai-draft-tags-${d.id}`} className="text-xs font-nav text-erf-dark/80">
          <strong>Suggested tags:</strong> {d.draft_tags?.length ? d.draft_tags.join(", ") : "—"}
          {d.confidence != null && <> · <strong>Confidence:</strong> {Math.round(d.confidence * 100)}%</>}
        </p>
      )}
      {d.transcript_text && <p className="text-xs font-nav text-erf-dark/80"><strong>Transcript:</strong> {d.transcript_text}</p>}
      {founder && !approved ? (
        <div className="space-y-2">
          <Input value={caption} onChange={(e) => setCaption(e.target.value)} maxLength={400} placeholder="Caption"
            aria-label="Caption to approve" className="min-h-[44px] bg-white text-erf-dark" data-testid={`portfolio-ai-caption-${d.id}`} />
          <Input value={alt} onChange={(e) => setAlt(e.target.value)} maxLength={280} placeholder="Alt text for screen readers"
            aria-label="Alt text to approve" className="min-h-[44px] bg-white text-erf-dark" data-testid={`portfolio-ai-alt-${d.id}`} />
          <label className="flex items-center gap-2 min-h-[44px] text-sm font-nav text-erf-dark">
            <input type="checkbox" checked={applySummary} onChange={(e) => setApplySummary(e.target.checked)}
              className="w-5 h-5" data-testid={`portfolio-ai-apply-summary-${d.id}`} />
            Also use the description as the project summary
          </label>
          <Button onClick={approve} disabled={busy} className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover"
            data-testid={`portfolio-ai-approve-${d.id}`}>Approve and use</Button>
        </div>
      ) : (
        <p className="text-sm font-nav text-erf-dark"><strong>Caption:</strong> {d.approved_caption ?? d.draft_caption ?? "—"}</p>
      )}
    </div>
  );
}

export function AiDraftPanel({ media, founder, onChanged }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get(`/admin/portfolio/media/${media.id}/ai_drafts`).then((r) => setItems(r.data.items)).catch(() => {});
  }, [media.id]);
  useEffect(() => { load(); }, [load]);
  const generate = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/portfolio/media/${media.id}/ai_draft`);
      toast.success("AI draft created — review it before approving.");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2" data-testid={`portfolio-ai-panel-${media.id}`}>
      {founder && (
        <Button variant="outline" onClick={generate} disabled={busy || media.processing_state !== "ready"}
          className="border-erf-borderCream text-erf-dark" data-testid={`portfolio-ai-generate-${media.id}`}>
          <Sparkles className="w-4 h-4 mr-1.5" aria-hidden="true" />{busy ? "Drafting…" : "Generate AI draft"}
        </Button>
      )}
      {items.length === 0 && <p className="text-xs font-nav text-erf-dark/70">No AI drafts yet.</p>}
      {items.map((d) => <DraftCard key={d.id} d={d} founder={founder} onApproved={() => { load(); onChanged?.(); }} />)}
    </div>
  );
}
